import axios, { type InternalAxiosRequestConfig } from 'axios'
import { router } from '@inertiajs/vue3'
import { reactive } from 'vue'

// The demo mode of the mentor tour (/mentor-tour). The tour draws the real
// member pages from a recording, for a visitor who can be signed in to an
// account of their own. So in demo mode the page must not talk to the server
// and must not leave: no request, no socket, no visit, no click, no typing.
//
// `shared.mentorTour` turns it on (MentorTour::Payload). Everything here is a
// no-op for a member.

// The answer to a request that a page sends after it opens (a list that
// loads as it scrolls, a calendar). The recording has the answer, so the
// request does not leave the page.
type RecordedRead = {
  path: string
  params: Record<string, string>
  data: unknown
}

export type MentorTourShared = {
  page: string
  realPath: string
  reads?: RecordedRead[]
}

export const MENTOR_TOUR_PATH = '/mentor-tour'
// The guide card and its buttons carry this attribute. They are the one part
// of the page that takes a click.
export const TOUR_UI_ATTRIBUTE = 'data-mentor-tour-ui'

// Reactive: the sidebar reads `realPath` in a computed, and the tour changes
// page with no reload.
const state = reactive({
  on: false,
  realPath: '',
  reads: [] as RecordedRead[],
})
let areGuardsInstalled = false

export const isMentorTour = () => state.on

// The first page of the tour has the address /mentor-tour, each other page
// /mentor-tour/<page>.
export const mentorTourPath = (page: string) =>
  page === 'home' ? MENTOR_TOUR_PATH : `${MENTOR_TOUR_PATH}/${page}`

const isTourPath = (path: string) =>
  path === MENTOR_TOUR_PATH || path.startsWith(`${MENTOR_TOUR_PATH}/`)

// The sidebar marks the current item from the address. A tour page is a page
// of the member app at a /mentor-tour address, so the sidebar asks here for
// the address the page has in the app.
export const mentorTourRealPath = (path: string) =>
  state.on && isTourPath(path) ? state.realPath : path

const isInTourUi = (target: EventTarget | null) =>
  target instanceof Element && !!target.closest(`[${TOUR_UI_ATTRIBUTE}]`)

const stop = (event: Event) => {
  if (!state.on || isInTourUi(event.target)) return
  event.stopPropagation()
  if (event.cancelable) event.preventDefault()
}

// The keys that move through a page stay. Every other key could type in a
// field or press a button.
const MOVE_KEYS = [
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  'Escape',
]
const stopKey = (event: KeyboardEvent) => {
  if (event.metaKey || event.ctrlKey || MOVE_KEYS.includes(event.key)) return
  stop(event)
}

const installGuards = () => {
  if (areGuardsInstalled || typeof document === 'undefined') return
  areGuardsInstalled = true

  // Capture on the document runs before a handler of the page. `mouseenter`
  // is here for the links that prefetch on hover. `contextmenu` and
  // `dragstart` are here for "open the link in a new tab": the links of a
  // recorded page do not go to a page of the visitor.
  for (const type of [
    'click',
    'auxclick',
    'dblclick',
    'submit',
    'mouseenter',
    'contextmenu',
    'dragstart',
    'paste',
    'drop',
  ]) {
    document.addEventListener(type, stop, true)
  }
  document.addEventListener('keydown', stopKey, true)

  router.on('before', (event) => {
    if (state.on && !isTourPath(event.detail.visit.url.pathname)) {
      event.preventDefault()
    }
  })
}

export const syncMentorTour = (shared?: MentorTourShared | null) => {
  state.on = !!shared
  state.realPath = shared?.realPath ?? ''
  state.reads = shared?.reads ?? []
  if (state.on) installGuards()
}

const findRead = (path: string, params: URLSearchParams) =>
  state.reads.find(
    (read) =>
      read.path === path &&
      Object.entries(read.params).every(
        ([key, value]) => (params.get(key) ?? '') === value,
      ),
  ) ??
  // A calendar asks for the weeks on its screen, so its dates are not the
  // recorded ones. A path with one recorded answer gives that answer.
  (state.reads.filter((read) => read.path === path).length === 1
    ? state.reads.find((read) => read.path === path)
    : undefined)

// For the request interceptor of axios (services/axios.ts). Inertia sends its
// visits through the same axios, so this one check covers both. A read of a
// tour page passes. A read with a recorded answer gets that answer. Every
// other request stops here.
export const cancelOutsideMentorTour = (config: InternalAxiosRequestConfig) => {
  if (!state.on) return config

  const url = new URL(
    config.url || '',
    config.baseURL || window.location.origin,
  )
  const method = (config.method || 'get').toLowerCase()
  const isOwnOrigin = url.origin === window.location.origin
  if (method === 'get' && isOwnOrigin && isTourPath(url.pathname)) return config

  if (method === 'get' && isOwnOrigin) {
    const params = new URLSearchParams(url.search)
    for (const [key, value] of Object.entries(config.params ?? {})) {
      params.set(key, value == null ? '' : String(value))
    }
    const read = findRead(url.pathname, params)
    if (read) {
      config.adapter = async () => ({
        data: read.data,
        status: 200,
        statusText: 'OK',
        headers: {},
        config,
      })
      return config
    }
  }
  throw new axios.CanceledError('mentor tour: demo mode', undefined, config)
}
